import {
  createColumnHelper,
  createPaginatedRowModel,
  createSortedRowModel,
  type RowData,
  rowPaginationFeature,
  rowSortingFeature,
  sortFn_alphanumeric,
  sortFn_basic,
  sortFn_datetime,
  sortFn_text,
  tableFeatures,
  useTable,
} from '@tanstack/react-table';
import { ArrowDownIcon, ArrowUpIcon, ChevronsUpDownIcon } from 'lucide-react';
import { type ReactNode, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/shared/lib/cn';
import { Button } from '@/shared/ui/button';
import {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/shared/ui/table';

type SortValue = string | number | Date | null | undefined;

export type DataTableColumn<TRow extends RowData> = {
  id: string;
  header: string;
  /** The value sorting uses, and what the cell shows unless `cell` is given. */
  value: (row: TRow) => SortValue;
  /** Custom cell content (a badge, a link, a formatted date…). */
  cell?: (row: TRow) => ReactNode;
  sortable?: boolean;
  align?: 'left' | 'right';
};

type DataTableProps<TRow extends RowData> = {
  /** Names the table for assistive technology; visually hidden unless `showCaption`. */
  caption: string;
  showCaption?: boolean;
  columns: DataTableColumn<TRow>[];
  data: TRow[];
  /** Rows per page. Pagination controls appear only when there is more than one page. */
  pageSize?: number;
  size?: 'default' | 'compact';
  /** Shown in a full-width row when `data` is empty. */
  empty?: ReactNode;
  getRowId?: (row: TRow) => string;
  className?: string;
};

const features = tableFeatures({
  rowSortingFeature,
  sortedRowModel: createSortedRowModel(),
  sortFns: {
    alphanumeric: sortFn_alphanumeric,
    basic: sortFn_basic,
    datetime: sortFn_datetime,
    text: sortFn_text,
  },
  rowPaginationFeature,
  paginatedRowModel: createPaginatedRowModel(),
});

/**
 * Client-side sortable, paginated table (TanStack Table v9 under the semantic `Table` parts).
 * Sort headers are buttons and the column reports `aria-sort`. Server-side modes are not wired.
 */
function DataTable<TRow extends RowData>({
  caption,
  showCaption = false,
  columns,
  data,
  pageSize = 25,
  size = 'default',
  empty,
  getRowId,
  className,
}: DataTableProps<TRow>) {
  const { t } = useTranslation();
  const byId = useMemo(() => new Map(columns.map((c) => [c.id, c])), [columns]);
  const tableColumns = useMemo(() => {
    const helper = createColumnHelper<typeof features, TRow>();
    return helper.columns(
      columns.map((column) =>
        helper.accessor((row) => column.value(row), {
          id: column.id,
          header: column.header,
          enableSorting: column.sortable ?? false,
          sortUndefined: 'last',
          sortDescFirst: false,
        }),
      ),
    );
  }, [columns]);

  const table = useTable({
    features,
    columns: tableColumns,
    data,
    initialState: { pagination: { pageIndex: 0, pageSize } },
    ...(getRowId ? { getRowId } : {}),
  });

  const rows = table.getRowModel().rows;
  const pageCount = table.getPageCount();
  const pageIndex = table.state.pagination.pageIndex;

  return (
    <div data-slot="data-table" className={cn('grid gap-3', className)}>
      <Table size={size}>
        <TableCaption className={showCaption ? undefined : 'sr-only'}>{caption}</TableCaption>
        <TableHeader>
          {table.getHeaderGroups().map((group) => (
            <TableRow key={group.id} className="hover:bg-transparent">
              {group.headers.map((header) => {
                const column = byId.get(header.column.id);
                const sorted = header.column.getIsSorted();
                const sortable = header.column.getCanSort();
                const next =
                  sorted === false
                    ? 'sortAscending'
                    : sorted === 'asc'
                      ? 'sortDescending'
                      : 'clearSort';
                return (
                  <TableHead
                    key={header.id}
                    scope="col"
                    className={column?.align === 'right' ? 'text-right' : undefined}
                    {...(sortable
                      ? {
                          'aria-sort':
                            sorted === 'asc'
                              ? 'ascending'
                              : sorted === 'desc'
                                ? 'descending'
                                : 'none',
                        }
                      : {})}
                  >
                    {sortable ? (
                      <button
                        type="button"
                        title={t(`table.${next}`)}
                        onClick={header.column.getToggleSortingHandler()}
                        className={cn(
                          '-mx-2 inline-flex cursor-pointer items-center gap-1 rounded-sm px-2 py-1 font-bold transition-colors duration-150 ease-standard hover:bg-accent',
                          column?.align === 'right' && 'flex-row-reverse',
                        )}
                      >
                        {column?.header}
                        {sorted === 'asc' ? (
                          <ArrowUpIcon aria-hidden="true" className="size-3.5" />
                        ) : sorted === 'desc' ? (
                          <ArrowDownIcon aria-hidden="true" className="size-3.5" />
                        ) : (
                          <ChevronsUpDownIcon
                            aria-hidden="true"
                            className="size-3.5 text-muted-foreground"
                          />
                        )}
                      </button>
                    ) : (
                      column?.header
                    )}
                  </TableHead>
                );
              })}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody>
          {rows.length === 0 ? (
            <TableRow className="hover:bg-transparent">
              <TableCell
                colSpan={columns.length}
                className="py-8 text-center text-muted-foreground"
              >
                {empty ?? t('table.empty')}
              </TableCell>
            </TableRow>
          ) : (
            rows.map((row) => (
              <TableRow key={row.id}>
                {row.getAllCells().map((cell) => {
                  const column = byId.get(cell.column.id);
                  const value = column?.value(row.original);
                  return (
                    <TableCell
                      key={cell.id}
                      className={column?.align === 'right' ? 'text-right tabular-nums' : undefined}
                    >
                      {column?.cell
                        ? column.cell(row.original)
                        : value instanceof Date
                          ? value.toISOString()
                          : value}
                    </TableCell>
                  );
                })}
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
      {pageCount > 1 ? (
        <nav
          aria-label={t('table.pagination')}
          className="flex items-center justify-end gap-3 text-sm text-muted-foreground"
        >
          <span aria-live="polite">
            {t('table.pageOf', { page: pageIndex + 1, pages: pageCount })}
          </span>
          <Button
            variant="outline"
            size="sm"
            disabled={!table.getCanPreviousPage()}
            onClick={() => table.previousPage()}
          >
            {t('table.previous')}
          </Button>
          <Button
            variant="outline"
            size="sm"
            disabled={!table.getCanNextPage()}
            onClick={() => table.nextPage()}
          >
            {t('table.next')}
          </Button>
        </nav>
      ) : null}
    </div>
  );
}

export { DataTable, type DataTableProps };
